/**
 * Maps a `seller-service-price` JSON:API listing into the display catalogue tree (ticket #5598).
 * Pure and framework-free, so it is unit-tested in isolation.
 *
 * The grouping into `Category → sub1 → sub2` and the synthesized ids come from the shared
 * {@link buildServiceTree}; this module adds what only the public catalogue needs per node:
 * the info-modal copy, the job-form booking target and the click-tracking payload.
 */

import {
  buildServiceTree,
  indexIncluded,
  mapPrice,
  ResolvedRef,
  ServiceTreeBuilders
} from 'Douleutaras/functional-components/ServicePrices/servicePriceTree';
import {
  SellerServicePriceAttributes,
  SellerServicePriceResource
} from 'Douleutaras/functional-components/ServicePrices/types';

import {
  CatalogueCategory,
  CatalogueNodeTracking,
  CatalogueService,
  JobFormQuestionAnswer,
  SellerServicePriceDocument,
  ServiceBookingTarget,
  ServiceInfo
} from './types';

// Blank copy must read as absent, not as an empty section: `null` hides the info affordance
const toInfoText = (value?: string | null): string | undefined =>
  value !== undefined && value !== null && value.trim() !== '' ? value : undefined;

const mapServiceInfo = (attrs: SellerServicePriceAttributes): ServiceInfo | undefined => {
  const includedExcluded = toInfoText(attrs.includedExcluded);
  const termsAndConditions = toInfoText(attrs.termsAndConditions);

  if (includedExcluded === undefined && termsAndConditions === undefined) {
    return undefined;
  }

  return { includedExcluded, termsAndConditions };
};

/** Coerces a form id (sent as a number or a numeric string) to a number; `undefined` when unusable. */
const toFormId = (value?: number | string | null): number | undefined => {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : undefined;
  }
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return undefined;
  }

  return Number(value);
};

/**
 * The job-form question/answer pair a sub1/sub2 record maps to, or `undefined` when the record
 * carries no (or a malformed) pair — the click then degrades to a coarser skip.
 *
 * There is no `answerId` attribute in the payload: a `choice-value` record **is** the answer, so
 * its own resource `id` is the chosen option id. Only `questionId` names the question it answers,
 * and a record without one (a `custom-service`/`custom-detail` the pro typed in) yields no pair.
 * The pair only resolves for a record that was actually sideloaded.
 */
const toQuestionAnswer = (ref?: ResolvedRef): JobFormQuestionAnswer | undefined => {
  const questionId = toFormId(ref?.resource?.attributes.questionId);
  const answerId = toFormId(ref?.resource?.id);

  return questionId !== undefined && answerId !== undefined ? { questionId, answerId } : undefined;
};

/**
 * The booking target for a row: the real category id plus the ancestor chain's pre-answers.
 *
 * The chain is truncated at the first level whose ids are missing. A deeper answer
 * cannot be applied without the shallower one it is conditioned on — so a detail without a
 * service pair degrades to the service-level skip, and a service without ids to category-only.
 */
const buildBookingTarget = (category: ResolvedRef, sub1?: ResolvedRef, sub2?: ResolvedRef): ServiceBookingTarget => {
  const pairs: Array<JobFormQuestionAnswer> = [];
  const sub1Pair = toQuestionAnswer(sub1);

  if (sub1Pair) {
    pairs.push(sub1Pair);
    const sub2Pair = toQuestionAnswer(sub2);

    if (sub2Pair) {
      pairs.push(sub2Pair);
    }
  }

  return { categoryId: category.id, pairs };
};

/**
 * The raw backend ids/titles for a node, as `Services Section Clicked` sends them.
 *
 * `isFormMapped` reads the deepest supplied ref's *own* pair rather than the booking chain's,
 * which {@link buildBookingTarget} truncates at the first missing level — a detail that does
 * carry form ids under a service that does not would otherwise report `false`.
 */
const buildTracking = (category: ResolvedRef, sub1?: ResolvedRef, sub2?: ResolvedRef): CatalogueNodeTracking => ({
  categoryId: category.id,
  categoryTitle: category.title,
  serviceId: sub1?.id,
  serviceTitle: sub1?.title,
  detailId: sub2?.id,
  detailTitle: sub2?.title,
  isFormMapped: toQuestionAnswer(sub2 ?? sub1) !== undefined
});

const catalogueBuilders: ServiceTreeBuilders<SellerServicePriceResource, CatalogueService, CatalogueCategory> = {
  createNode: ({ serviceId, level, label, categoryId, category, sub1, sub2 }) => ({
    serviceId,
    categoryId,
    level,
    label,
    booking: buildBookingTarget(category, sub1, sub2),
    tracking: buildTracking(category, sub1, sub2)
  }),
  applyRow: (node, row) => ({
    ...node,
    price: mapPrice(row.attributes),
    info: mapServiceInfo(row.attributes)
  }),
  createCategory: (category, categoryId, services) => ({
    categoryId,
    label: category.title,
    // Only the sideloaded `categories` record carries this; sub1/sub2 info rides on the price row
    info: toInfoText(category.resource?.attributes.servicePriceInfo),
    services,
    tracking: buildTracking(category)
  })
};

/** Maps the public `service-price` listing into the display catalogue tree consumed by {@link ServicesCatalogue}. */
export const mapServicePricesToCatalogue = (doc: SellerServicePriceDocument): { categories: Array<CatalogueCategory> } => ({
  categories: buildServiceTree(doc.data, indexIncluded(doc.included), catalogueBuilders)
});
